import React, { Dispatch, SetStateAction, useEffect, useRef } from 'react';
import { Box, IconButton, Stack, TextField, InputAdornment } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Search } from '@mui/icons-material';

interface AdditionalCostGridToolbarProps {
    filter: string;
    setFilter: Dispatch<SetStateAction<string>>;
    handleRefresh?: () => void;
}

const AdditionalCostGridToolbar: React.FC<AdditionalCostGridToolbarProps> = ({
    filter,
    setFilter,
    handleRefresh
}) => {
    const [localSearchInput, setLocalSearchInput] = React.useState(filter);
    const debounceTimer = useRef<NodeJS.Timeout>();
    const inputRef = useRef<HTMLInputElement>(null);
    const [isFocused, setIsFocused] = React.useState(false);
    const [cursorPosition, setCursorPosition] = React.useState(0);

    useEffect(() => {
        setLocalSearchInput(filter);
    }, [filter]);

    useEffect(() => {
        if (debounceTimer.current) {
            clearTimeout(debounceTimer.current);
        }
        
        debounceTimer.current = setTimeout(() => {
            if (localSearchInput !== filter) {
                setFilter(localSearchInput);
            }
        }, 1);

        return () => {
            if (debounceTimer.current) {
                clearTimeout(debounceTimer.current);
            }
        };
    }, [localSearchInput, filter, setFilter]);

    useEffect(() => {
        if (isFocused && inputRef.current && document.activeElement !== inputRef.current) {
            inputRef.current.focus();
            inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
        }
    });

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.focus();
            setIsFocused(true);
        }
    }, []);

    return (
        <Box>
            <Stack
                direction="row"
                spacing={2}
                alignItems="center"
                sx={{ 
                    justifyContent: 'flex-start',
                    flexWrap: 'wrap',
                    gap: 2
                }}
            >
                {/* Search Input */}
                <TextField
                    inputRef={inputRef}
                    sx={{
                        width: {
                            xs: '100px',
                            sm: '180px',
                            md: '220px',
                            lg: '260px',
                        },
                    }}
                    aria-label="Search"
                    placeholder="Search additional costs"
                    size="small"
                    variant="outlined"
                    value={localSearchInput}
                    onChange={(e) => {
                        setLocalSearchInput(e.target.value);
                        setCursorPosition(e.target.selectionStart || 0);
                    }}
                    onFocus={() => setIsFocused(true)}
                    onBlur={() => setIsFocused(false)}
                    onSelect={(e) => setCursorPosition(e.currentTarget.selectionStart || 0)}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <Search fontSize="small" />
                                </InputAdornment>
                            ),
                        },
                    }}
                />

                {/* Spacer to push refresh button to the right */}
                <Box sx={{ flexGrow: 1 }} />

                {/* Refresh Button - always last */}
                <IconButton 
                    color="primary" 
                    onClick={handleRefresh}
                    sx={{
                        border: '1px dotted',
                        borderColor: 'primary.main',
                        paddingTop: 1,
                    }}
                >
                    <RefreshIcon />
                </IconButton>
            </Stack>
        </Box>
    );
};

export default AdditionalCostGridToolbar;